import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import {
  RiColorFilterLine,
  RiInformationLine,
  RiStackLine,
  RiArrowDownSLine,
  RiEyeLine,
} from "@remixicon/react";
import { useEffect } from "react";
import { ViewMode } from "../../types";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";

interface Props {
  options: { key: string; label: string; info?: string }[];
  value: string;
  onChange: (value: string) => void;
  creativeType: string | null;
  viewMode: ViewMode;
}
const LabelView = ({ options, value, onChange, creativeType, viewMode }: Props) => {
  const selectedOption = options.find((o) => o.key === value);
  useEffect(() => {
    const _selectedOption = options.find((o) => o.key === value);
    const _multipleOption = options.find((option) => option.key === "multiple");

    if (creativeType !== "video") {
      if (_multipleOption && value !== "multiple") {
        onChange("multiple");
      }
    } else if (!_selectedOption) {
      // For video creative_type, check if selected option exists, otherwise fallback
      const defaultOption = _multipleOption || options[0];
      onChange(defaultOption.key);
    }
  }, [creativeType, value, onChange, options]);

  return (
    <SqSelect value={value} onValueChange={onChange}>
      <SqSelectTrigger
        chevronClassName="hidden"
        className="h-8 border-0 p-1 text-brand-primary-new"
      >
        <div className={`flex gap-[6px] items-center p-1 w-full h-6`}>
          <RiEyeLine size={14} color={`#9AA3BD`} className="shrink-0" />
          <span className="mt-[1px] shrink-0 overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-medium leading-none tracking-[0.24px]">
            Label View
          </span>
          <span className="mt-[1px] flex-1 truncate text-brand-primary-600 text-[12px] font-normal leading-none tracking-[0.24px]">
            {selectedOption?.label}
          </span>
          <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
        </div>
      </SqSelectTrigger>
      <SqSelectContent radius={12}>
        {options?.map((option) => {
          const isDisabled =
            option.key === "sequence" &&
            (creativeType !== "video" ||
              viewMode === "line_chart_view" ||
              viewMode === "creative_share_view");

          const optionItem = (
            <SqSelectItem
              key={option.key}
              value={option.key}
              className={`cursor-pointer flex justify-between [&_span]:w-full`}
              hideItemIndicator
              disabled={isDisabled}
            >
              <div className="flex items-center justify-between w-[100%]">
                {option.key === "sequence" ? (
                  <RiStackLine
                    size={14}
                    className="shrink-0 text-brand-primary-400 mr-2"
                  />
                ) : option.key === "multiple" ? (
                  <RiColorFilterLine
                    size={14}
                    className="shrink-0 text-brand-primary-400 mr-2"
                  />
                ) : null}
                <span className="flex text-brand-primary-new text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                  {option.label}
                </span>
                <SqTooltipProvider>
                  <SqTooltip>
                    <SqTooltipTrigger>
                      <RiInformationLine size={14} color="#9AA3BD" className="shrink-0" />
                    </SqTooltipTrigger>
                    <SqTooltipContent
                      side="bottom"
                      align="start"
                      alignOffset={100}
                      className="text-[12px] w-[220px]"
                      portal={true}
                    >
                      {option.info}
                    </SqTooltipContent>
                  </SqTooltip>
                </SqTooltipProvider>
              </div>
            </SqSelectItem>
          );
          if (isDisabled)
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">{optionItem}</div>
                  </TooltipTrigger>
                  <Portal>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      alignOffset={100}
                      className="text-[12px] w-[220px]"
                    >
                      {viewMode === "line_chart_view"
                        ? "Line Chart isn't supported in Sequence."
                        : viewMode === "creative_share_view"
                          ? "Creative Share isn't supported in Sequence."
                          : creativeType !== "video"
                            ? "Sequences are Video-only."
                            : ""}
                    </TooltipContent>
                  </Portal>
                </Tooltip>
              </TooltipProvider>
            );
          return optionItem;
        })}
      </SqSelectContent>
    </SqSelect>
  );
};

export default LabelView;
